import { View, StyleSheet } from "react-native";
import React, { useMemo } from "react";
import { Text } from "@ui-kitten/components";
import { useAppSelector } from "hooks/useAppSelector";
import { useNavigation, NavigationProp } from "@react-navigation/native";
import { MainFlowParamList } from "navigation/types";
import { Pressable } from "react-native";
import WhiteText from "components/WhiteText";
import i18n from "plugins/localization";
import { Image } from "expo-image";
import {
  EXPERIENCE_EVENT_TYPES,
  isSmallScreen,
  NON_SPORTS_EVENT_TYPES,
} from "constants/";
import { AccountTicketDetails } from "features/types";
import useMarketingAssetsSections from "hooks/useMarketingAssetsSections";
import { formatEventDate } from "utils/eventUtils";
import { svgURL } from "utils/fileUtils";
const LOGO_SIZE = 125;

const MyReservations = ({
  accountEvents,
}: {
  accountEvents: AccountTicketDetails[];
}) => {
  const { tenantThemeColor, tenantSecondaryColor } = useAppSelector(
    (state) => state.tenant
  );
  const upcomingEvent = accountEvents[0];
  const navigation = useNavigation<NavigationProp<MainFlowParamList>>();
  const eventType = upcomingEvent.event.type;

  const { isSectionMarketingAssets } = useMarketingAssetsSections(
    upcomingEvent.tickets[0].planPackageId
  );

  const shouldShowNumberOfSeats = useMemo(() => {
    if (upcomingEvent.premiumClass === null) return true;
    if (upcomingEvent.event.type === "EXPERIENCE") return false;
    if (isSectionMarketingAssets(upcomingEvent.tickets[0].seat.section))
      return false;
    return true;
  }, [upcomingEvent]);

  const upcomingEventTickets = upcomingEvent.groupCode.tickets.filter((t) =>
    ["RESERVED", "ISSUED", "SCANNED"].includes(t.status)
  );

  const nonSportsEvent = NON_SPORTS_EVENT_TYPES.includes(eventType);
  return (
    <Pressable
      onPress={() => {
        navigation.navigate("MyEvents", {
          screen: "EventDetail",
          params: {
            groupCode: upcomingEvent.groupCode.groupCode,
          },
        });
      }}
    >
      <View style={styles.bannerContainer}>
        <View
          style={[
            styles.leftContainer,
            { backgroundColor: tenantSecondaryColor },
          ]}
        >
          {nonSportsEvent ? (
            <WhiteText category="s2">{upcomingEvent.event.name}</WhiteText>
          ) : (
            <WhiteText category="s2">My Next Game</WhiteText>
          )}
          {!EXPERIENCE_EVENT_TYPES.includes(upcomingEvent.event.type) && (
            <>
              <WhiteText
                category="s2"
                status="control"
                style={{ marginVertical: 4 }}
              >
                {formatEventDate(upcomingEvent.event, "hh:mm A")}
              </WhiteText>
              <WhiteText status="control" category="s2">
                {formatEventDate(upcomingEvent.event, "ddd, MMM DD")}
              </WhiteText>
              {shouldShowNumberOfSeats && (
                <WhiteText category="h6" style={{ marginTop: 4 }}>
                  {`${upcomingEventTickets.length} ${i18n.t("common.seat", {
                    count: upcomingEventTickets.length,
                  })}`}
                </WhiteText>
              )}
            </>
          )}
        </View>
        <View
          style={[styles.rightContainer, { backgroundColor: tenantThemeColor }]}
        >
          {nonSportsEvent ? (
            <Image
              source={{ uri: upcomingEvent.event.imageUrl || undefined }}
              style={{ aspectRatio: isSmallScreen ? 16 / 9 : 21 / 9 }}
              contentFit="fill"
            ></Image>
          ) : (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <>
                <View style={styles.tenantLogo}>
                  <Image
                    source={{
                      uri: svgURL(upcomingEvent.event.hostName),
                    }}
                    style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                    responsivePolicy="initial"
                    contentFit="contain"
                  ></Image>
                </View>
                <View>
                  <View style={styles.topLine} />
                  <Text
                    style={[
                      styles.vsText,
                      {
                        color: tenantSecondaryColor,
                      },
                    ]}
                  >
                    VS
                  </Text>
                  <View style={styles.bottomLine} />
                </View>
              </>
              <View style={styles.opponentLogo}>
                <Image
                  source={{
                    uri: svgURL(upcomingEvent.event.opponentName),
                  }}
                  style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                  responsivePolicy="initial"
                  contentFit="contain"
                ></Image>
              </View>
            </View>
          )}
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  bannerContainer: {
    flexDirection: "row",
    borderRadius: 15,
    overflow: "hidden",
  },
  leftContainer: {
    flex: 0.32,
    paddingHorizontal: 10,
    paddingVertical: 25,
    justifyContent: "center",
    alignItems: "center",
  },
  rightContainer: {
    flex: 0.68,
  },
  tenantLogo: {
    opacity: 0.5,
    transform: [
      {
        translateX: 10,
      },
      {
        translateY: -20,
      },
    ],
  },
  opponentLogo: {
    zIndex: -1,
    opacity: 0.5,
    transform: [
      {
        translateX: 0,
      },
      {
        translateY: 40,
      },
    ],
  },
  topLine: {
    position: "absolute",
    right: -12,
    top: -22,
    width: 40,
    height: 2,
    backgroundColor: "white",
    transform: [{ rotate: "-75deg" }],
    opacity: 0.5,
  },
  bottomLine: {
    position: "absolute",
    right: 2,
    bottom: -23,
    width: 40,
    height: 2,
    backgroundColor: "white",
    transform: [{ rotate: "-75deg" }],
    opacity: 0.5,
  },
  vsText: {
    fontSize: 24,
    fontWeight: "bold",
  },
  numberOfSeatsChip: {
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
    bottom: 10,
    right: 10,
  },
});

export default MyReservations;
